import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { InvoiceViewModel } from '../../models/Invoice/InvoiceViewModel';
import api from '../../services/api';
import InvoiceStatusState from '../../enums/InvoiceStatusState';
import { useAuth } from '../../contexts/auth';

interface InvoiceActionButtonsProps {
  invoice: InvoiceViewModel;
  refreshInvoice: () => void;
}

type InvoiceStatusModel = {
  id: number;
  label: string;
  state: InvoiceStatusState;
};

export default function InvoiceActionButtons({
  invoice,
  refreshInvoice,
}: InvoiceActionButtonsProps) {
  const {
    user: { organization },
  } = useAuth();

  const [isChangingState, setIsChangingState] = useState(false);
  const [allowedChanges, setAllowedChanges] = useState<number[]>([]);
  const [statusStates, setStatusStates] = useState<InvoiceStatusModel[]>([]);
  const [intendedRoute, setIntendedRoute] = useState('');

  async function getInvoiceStateAllowedChanges() {
    setIsChangingState(true);
    const { data } = await api.get(
      `organizations/${organization.id}/invoices/${invoice.id}/allowed-changes`
    );
    setAllowedChanges(data.data);
    setIsChangingState(false);
  }

  async function getStatusStates(state: InvoiceStatusState) {
    const { data } = await api.get(
      `organizations/${organization.id}/invoice-statuses?state=${state}`
    );
    setStatusStates(data.data);
  }

  async function updateInvoiceState(route: string) {
    setIsChangingState(true);
    try {
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/${route}`
      );
      refreshInvoice();
    } catch (e: any) {
      setIsChangingState(false);
      if (e?.response?.status === 409) {
        toast.error(
          e?.response?.data?.errors.reduce(
            (acc: string, error: string) => acc + error,
            ''
          )
        );
      } else {
        toast.error('Houve um problema ao processar a sua solicitação');
      }
      console.error(e);
    } finally {
      setStatusStates([]);
    }
  }

  useEffect(() => {
    getInvoiceStateAllowedChanges();
  }, [invoice]);

  const changeStatus = (status: number, route: string) => {
    if (allowedChanges.includes(status)) {
      if (
        // eslint-disable-next-line no-restricted-globals
        confirm(
          'Você deseja alterar o status da Ordem de Serviço? Essa ação não poderá ser desfeita'
        )
      ) {
        updateInvoiceState(route);
      }
    } else {
      toast.error('Não é possível atualizar esta Ordem de Serviço');
    }
  };

  return (
    <>
      {isChangingState ? (
        <div className='d-flex placeholder-glow text-end'>
          <span className='placeholder col-3 mx-1'>&nbsp;</span>
          <span className='placeholder col-1 mx-1'>&nbsp;</span>
          <span className='placeholder col-2 mx-1'>&nbsp;</span>
        </div>
      ) : (
        <div className='d-flex justify-content-end gap-2'>
          {allowedChanges.includes(InvoiceStatusState.Wip) && (
            <button
              type='button'
              className={`btn ${
                invoice.startedAt ? 'btn-outline-secondary' : 'btn-primary'
              }`}
              onClick={() => changeStatus(InvoiceStatusState.Wip, 'start')}
            >
              {invoice.startedAt ? 'Reabrir' : 'Iniciar'}
            </button>
          )}
          {allowedChanges.includes(InvoiceStatusState.Todo) && (
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => changeStatus(InvoiceStatusState.Todo, 'todo')}
            >
              Marcar como Não Iniciado
            </button>
          )}
          {allowedChanges.includes(InvoiceStatusState.Blocked) && (
            <button
              type='button'
              className='btn btn-warning text-black'
              data-bs-toggle='modal'
              data-bs-target='#invoice-state1-modal'
              onClick={() => {
                setIntendedRoute('block');
                getStatusStates(InvoiceStatusState.Blocked);
              }}
            >
              Bloquear
            </button>
          )}
          {allowedChanges.includes(InvoiceStatusState.Canceled) && (
            <button
              type='button'
              className='btn btn-dark'
              data-bs-toggle='modal'
              data-bs-target='#invoice-state1-modal'
              onClick={() => {
                setIntendedRoute('cancel');
                getStatusStates(InvoiceStatusState.Canceled);
              }}
            >
              Cancelar
            </button>
          )}
          {allowedChanges.includes(InvoiceStatusState.Completed) && (
            <button
              type='button'
              className='btn btn-outline-success'
              onClick={() =>
                changeStatus(InvoiceStatusState.Completed, 'complete')
              }
            >
              Concluir
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      <div
        className='modal'
        tabIndex={-1}
        id='invoice-state1-modal'
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        aria-labelledby='staticBackdropLabel'
        aria-hidden='true'
      >
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Especifique o motivo</h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'
              />
            </div>
            <div className='modal-body text-start'>
              <div className='mb-3'>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className='form-label'>Selecione o motivo</label>
                <select
                  required
                  className='form-select'
                  aria-label='Selecione o motivo'
                  onChange={(event) => {
                    setIntendedRoute(`${intendedRoute}/${event.target.value}`);
                  }}
                >
                  <option value=''>Selecione o motivo</option>
                  {statusStates.map((s) => (
                    <option value={s.id} key={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className='text-end mt-3'>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-bs-dismiss='modal'
                  onClick={() => {
                    updateInvoiceState(intendedRoute);
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
